import { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  fetchWatch, fetchValidation, fetchServices, fetchParts, deleteWatch, archiveWatch, unarchiveWatch, unlistWatch, fetchWatches,
} from "@/lib/api";
import { errorMessage } from "@/lib/errors";
import WatchFormSheet from "@/components/WatchFormSheet";
import WatchMediaPanel from "@/components/WatchMediaPanel";
import WatchThumb from "@/components/WatchThumb";
import RelatedDocumentsPanel from "@/components/RelatedDocumentsPanel";
import EbayTemplateDialog from "@/components/EbayTemplateDialog";
import { listingAgeLabel, isStaleListing } from "@/lib/listing";
import ServiceFormSheet from "@/components/ServiceFormSheet";
import InspectionSheet from "@/components/InspectionSheet";
import PartFormSheet from "@/components/PartFormSheet";
import ServicesTable from "@/components/ServicesTable";
import PartsTable from "@/components/PartsTable";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ArrowLeft, ArrowRight, Pencil, Plus, Wrench, Package, Camera, Tag, Trash2, Archive, ArchiveRestore, CircleOff, Globe, Link2, ClipboardList } from "lucide-react";
import { toast } from "sonner";

const fmtMoney = (n) =>
  n === null || n === undefined || n === ""
    ? "—"
    : new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);

function statusTone(s) {
  if (!s) return "bg-[#2B2214]/[0.14] text-[#3A2A10] border-[#8B6A1F]/45";
  const lower = s.toLowerCase();
  if (lower === "running" || lower === "overhauled" || lower.includes("runs"))
    return "bg-emerald-500/10 text-emerald-400 border-emerald-500/20";
  if (lower === "sold") return "bg-neutral-500/10 text-[#3A2A10] border-neutral-500/20";
  if (lower.includes("bad") || lower.includes("broken") || lower.includes("missing"))
    return "bg-rose-500/10 text-rose-400 border-rose-500/20";
  if (lower.includes("needs") || lower.includes("process") || lower.includes("slow"))
    return "bg-amber-500/10 text-amber-400 border-amber-500/20";
  if (lower.includes("for sale")) return "bg-[#D4AF37]/10 text-[#3A2A10] border-[#D4AF37]/20";
  return "bg-[#2B2214]/[0.14] text-[#0A0604] border-[#8B6A1F]/45";
}

export default function WatchDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [w, setW] = useState(null);
  const [loading, setLoading] = useState(true);
  const [validation, setValidation] = useState({});
  const [services, setServices] = useState([]);
  const [parts, setParts] = useState([]);
  // Sibling list derived from the same filters the user had in /inventory.
  // We fetch up to 500 so keyboard/click prev-next feels infinite even on
  // big filtered sets. Only fires when at least one filter/search is in
  // the URL — otherwise we don't want to flash an arbitrary ordering.
  const [siblings, setSiblings] = useState([]);
  const siblingParams = useMemo(() => {
    const ks = ["q", "brand", "status", "caliber", "movement_type", "bin_location", "sort", "order", "hideSold"];
    if (!ks.some((k) => sp.get(k) !== null || sp.getAll(k).length)) return null;
    const p = { limit: 500 };
    if (sp.get("q")) p.q = sp.get("q");
    if (sp.get("brand")) p.brand = sp.get("brand");
    if (sp.getAll("status").length) p.status = sp.getAll("status");
    if (sp.getAll("caliber").length) p.caliber = sp.getAll("caliber");
    if (sp.get("movement_type")) p.movement_type = sp.get("movement_type");
    if (sp.get("bin_location")) p.bin_location = sp.get("bin_location");
    if (sp.get("sort")) p.sort = sp.get("sort");
    if (sp.get("order")) p.order = sp.get("order");
    if (sp.get("hideSold") !== "0") p.status_exclude = ["SOLD"];
    return p;
  }, [sp]);

  useEffect(() => {
    if (!siblingParams) { setSiblings([]); return; }
    fetchWatches(siblingParams).then((r) => setSiblings(r.items || [])).catch(() => setSiblings([]));
  }, [siblingParams]);

  const { prevId, nextId, position, total } = useMemo(() => {
    if (!siblings.length) return { prevId: null, nextId: null, position: null, total: 0 };
    const idx = siblings.findIndex((x) => x.id === id);
    return {
      prevId: idx > 0 ? siblings[idx - 1].id : null,
      nextId: idx >= 0 && idx < siblings.length - 1 ? siblings[idx + 1].id : null,
      position: idx >= 0 ? idx + 1 : null,
      total: siblings.length,
    };
  }, [siblings, id]);

  // Keep the filter query string when navigating siblings so the chain
  // keeps working forward and back.
  const goSibling = useCallback((targetId) => {
    if (!targetId) return;
    nav(`/inventory/${targetId}?${sp.toString()}`);
  }, [nav, sp]);

  const [editOpen, setEditOpen] = useState(false);
  const [ebayOpen, setEbayOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [tab, setTab] = useState("services");

  const doDelete = async () => {
    try {
      await deleteWatch(id);
      toast.success("Watch deleted");
      nav("/inventory");
    } catch (err) {
      toast.error(errorMessage(err, "Failed to delete"));
    }
  };

  const toggleArchive = async () => {
    try {
      if (w?.archived) {
        await unarchiveWatch(id);
        toast.success("Restored from archive");
      } else {
        await archiveWatch(id);
        toast.success("Archived — hidden from Inventory & analytics", {
          action: {
            label: "Undo",
            onClick: () => unarchiveWatch(id).then(loadWatch).catch(() => toast.error("Undo failed")),
          },
        });
      }
      loadWatch();
    } catch (err) {
      toast.error(errorMessage(err, "Archive failed"));
    }
  };

  const togglePublic = async () => {
    try {
      const { makePublic, makeUnpublic } = await import("@/lib/api");
      const next = !w?.public;
      // Use dedicated endpoints (mirror archive/unarchive shape) — these
      // do a surgical $set on `public` + `public_since` ONLY. They never
      // touch any other field, even if some future bug breaks the general
      // PUT path. Lesson learned the hard way 2026-05-07.
      if (next) {
        await makePublic(id);
      } else {
        await makeUnpublic(id);
      }
      if (next) {
        // Public link uses the user-facing excel_id (e.g. /portfolio/657)
        // when available — much easier to remember & share than the UUID.
        // Backend accepts either form. Falls back to UUID if no excel_id.
        const slug = w?.excel_id ?? id;
        const url = `${window.location.origin}/portfolio/${slug}`;
        try { await navigator.clipboard.writeText(url); } catch { /* clipboard may be denied */ }
        toast.success("Public link enabled & copied", {
          description: url,
          action: {
            label: "Disable",
            onClick: async () => {
              try { await makeUnpublic(id); loadWatch(); }
              catch (err) { toast.error(errorMessage(err, "Could not disable")); }
            },
          },
        });
      } else {
        toast.success("Public link disabled");
      }
      loadWatch();
    } catch (err) {
      toast.error(errorMessage(err, "Toggle failed"));
    }
  };

  const copyPublicLink = async () => {
    const slug = w?.excel_id ?? id;
    const url = `${window.location.origin}/portfolio/${slug}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Public link copied", { description: url });
    } catch {
      toast.info(url);
    }
  };

  const doUnlist = async () => {
    // Preserve current listing fields AND status so Undo can restore them
    // (unlist also auto-flips "For Sale" status → "Owned").
    const prior = {
      listing_channel: w?.listing_channel,
      listing_price: w?.listing_price,
      listed_on: w?.listed_on,
      status: w?.status,
    };
    try {
      await unlistWatch(id);
      toast.success("Unlisted — removed from the For Sale feed", {
        action: {
          label: "Undo",
          onClick: async () => {
            try {
              await import("@/lib/api").then(({ updateWatch }) => updateWatch(id, prior));
              loadWatch();
            } catch {
              toast.error("Undo failed");
            }
          },
        },
      });
      loadWatch();
    } catch (err) {
      toast.error(errorMessage(err, "Unlist failed"));
    }
  };
  const [svcSheet, setSvcSheet] = useState({ open: false, item: null });
  const [inspectionOpen, setInspectionOpen] = useState(false);
  const [partSheet, setPartSheet] = useState({ open: false, item: null });

  const loadWatch = useCallback(async () => {
    try {
      const d = await fetchWatch(id);
      setW(d);
    } catch {
      toast.error("Watch not found");
      nav("/inventory");
    } finally {
      setLoading(false);
    }
  }, [id, nav]);

  const loadServices = useCallback(async () => {
    const r = await fetchServices({ watch_id: id, limit: 500 });
    setServices(r.items);
  }, [id]);

  const loadParts = useCallback(async () => {
    const r = await fetchParts({ watch_id: id, limit: 500 });
    setParts(r.items);
  }, [id]);

  useEffect(() => { fetchValidation().then(setValidation); }, []);
  useEffect(() => { loadWatch(); }, [loadWatch]);
  useEffect(() => { loadServices(); loadParts(); }, [loadServices, loadParts]);

  // Keyboard: ← / → walks prev/next through the filtered list. Ignored when
  // the user is typing in a form field so editing dates/notes stays sane.
  useEffect(() => {
    const handler = (e) => {
      if (!prevId && !nextId) return;
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select" || e.target?.isContentEditable) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === "ArrowLeft" && prevId) { e.preventDefault(); goSibling(prevId); }
      else if (e.key === "ArrowRight" && nextId) { e.preventDefault(); goSibling(nextId); }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [prevId, nextId, goSibling]);

  if (loading || !w) {
    return <div className="px-10 py-20 text-[#3A2A10]" data-testid="watch-detail-loading">Loading…</div>;
  }

  const partsSpend = w.parts || 0;

  const servicesSpend = services.reduce((s, x) => s + (x.cost || 0), 0);

  return (
    <div className="px-6 md:px-10 py-8 pb-28 max-w-[1600px]" data-testid="watch-detail">
      <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
        <Link
          to={`/inventory?${sp.toString()}`}
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#3A2A10] hover:text-[#0A0604] transition-colors"
          data-testid="back-to-inventory"
        >
          <ArrowLeft size={14} /> All Watches
        </Link>
        {position !== null && total > 1 && (
          <div className="inline-flex items-center gap-2 text-xs text-[#3A2A10]" data-testid="watch-nav">
            <button
              onClick={() => goSibling(prevId)}
              disabled={!prevId}
              className="inline-flex items-center gap-1 h-8 px-2.5 border border-[#8B6A1F]/45 rounded-sm uppercase tracking-wider disabled:opacity-30 hover:bg-[#2B2214]/[0.06]"
              title="Previous in list (← arrow)"
              data-testid="watch-nav-prev"
            >
              <ArrowLeft size={12} /> Prev
            </button>
            <span className="font-mono-data text-[#0A0604]" data-testid="watch-nav-position">
              {position} / {total}
            </span>
            <button
              onClick={() => goSibling(nextId)}
              disabled={!nextId}
              className="inline-flex items-center gap-1 h-8 px-2.5 border border-[#8B6A1F]/45 rounded-sm uppercase tracking-wider disabled:opacity-30 hover:bg-[#2B2214]/[0.06]"
              title="Next in list (→ arrow)"
              data-testid="watch-nav-next"
            >
              Next <ArrowRight size={12} />
            </button>
          </div>
        )}
      </div>

      <div className="mb-8">
        <div className="flex items-center gap-3 mb-3 flex-wrap">
          <span className="font-mono-data text-[#0A0604] text-2xl font-bold leading-none border border-[#D4AF37]/40 px-3 py-1.5 rounded-sm bg-[#D4AF37]/5" data-testid="detail-watch-id">
            ID #{w.excel_id ?? "—"}
          </span>
          {w.status && (
            <span className={`inline-block text-[10px] uppercase tracking-wider px-2 py-1 border rounded-sm ${statusTone(w.status)}`}>
              {w.status}
            </span>
          )}
          {w.bin_location && (
            <span className="text-[10px] uppercase tracking-wider text-[#3A2A10]">
              Location: <span className="text-[#0A0604] font-mono-data">{w.bin_location}</span>
            </span>
          )}
          {w.listing_channel && (
            <span
              className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider px-2 py-1 border rounded-sm ${isStaleListing(w.listed_on) ? "bg-rose-500/10 text-rose-500 border-rose-500/30" : "bg-[#D4AF37]/10 text-[#3A2A10] border-[#D4AF37]/30"}`}
              data-testid="detail-listing-badge"
            >
              <Tag size={10} />
              {w.listing_channel}
              {w.listed_on && (
                <span className="font-mono-data opacity-80">· {listingAgeLabel(w.listed_on)}</span>
              )}
            </span>
          )}
          {w.ebay_order_id && (
            <span
              className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider px-2 py-1 border border-[#8B6A1F]/40 bg-[#2B2214]/[0.06] rounded-sm text-[#3A2A10]"
              data-testid="detail-ebay-order-id"
              title="eBay order ID — groups all items sold together"
            >
              <Tag size={10} />
              Order <span className="font-mono-data text-[#0A0604]">{w.ebay_order_id}</span>
            </span>
          )}
          {w.ebay_item_id && (
            <span
              className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider px-2 py-1 border border-[#8B6A1F]/40 bg-[#2B2214]/[0.06] rounded-sm text-[#3A2A10]"
              data-testid="detail-ebay-item-id"
              title="eBay item ID — uniquely identifies THIS watch for analytics joins"
            >
              <Tag size={10} />
              Item <span className="font-mono-data text-[#0A0604]">{w.ebay_item_id}</span>
            </span>
          )}
          {w.public && (
            <span
              className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider px-2 py-1 border border-[#D4AF37]/50 bg-[#D4AF37]/10 rounded-sm text-[#3A2A10]"
              data-testid="detail-public-badge"
              title="This watch has a public restoration page anyone with the link can view"
            >
              <Globe size={10} /> Public
            </span>
          )}
        </div>
        <div className="flex items-center gap-5 flex-wrap">
          <h1 className="font-serif-display text-4xl sm:text-5xl text-[#0A0604] leading-none mb-2" data-testid="detail-watch-brand">
            {w.brand || "Unbranded"}
          </h1>
          <WatchThumb
            watch={w}
            size="md"
            onClick={() => setTab("media")}
          />
        </div>
        <div className="text-sm text-[#3A2A10]">
          {w.model || "—"}
          {w.caliber && <span className="text-[#3A2A10]"> · Caliber </span>}
          {w.caliber && <span className="font-mono-data text-[#0A0604]">{w.caliber}</span>}
          {w.mfg && <span className="text-[#3A2A10]"> · {w.mfg}</span>}
          {w.year_mfg && <span className="text-[#3A2A10]"> · {w.year_mfg}</span>}
        </div>
        <div className="mt-5 flex flex-wrap items-center gap-2">
          <Button
            onClick={() => setEditOpen(true)}
            variant="outline"
            className="bg-transparent border-[#8B6A1F]/45 hover:bg-[#2B2214]/[0.14] hover:text-[#3A2A10] rounded-sm h-8"
            data-testid="edit-watch-btn"
          >
            <Pencil size={13} className="mr-1.5" /> Edit Watch
          </Button>
          <Button
            onClick={() => setInspectionOpen(true)}
            variant="outline"
            className="bg-transparent border-[#8B6A1F]/45 hover:bg-[#2B2214]/[0.14] hover:text-[#3A2A10] rounded-sm h-8"
            data-testid="inspection-btn"
          >
            <ClipboardList size={13} className="mr-1.5" /> Inspection
          </Button>
          <Button
            onClick={() => setEbayOpen(true)}
            variant="outline"
            className="bg-transparent border-[#8B6A1F]/45 hover:bg-[#2B2214]/[0.14] hover:text-[#3A2A10] rounded-sm h-8"
            data-testid="ebay-template-btn"
          >
            <Tag size={13} className="mr-1.5" /> eBay Template
          </Button>
          {(w.listing_channel || w.listing_price || w.listed_on || /for sale/i.test(w.status || "")) && (
            <Button
              onClick={doUnlist}
              variant="outline"
              className="bg-transparent border-[#8B6A1F]/45 text-[#3A2A10] hover:bg-[#2B2214]/[0.08] hover:text-[#0A0604] rounded-sm h-8 ml-auto"
              data-testid="detail-unlist-watch-btn"
              title="Clear listing channel, price & date — and flip For-Sale status to Owned. Drops this watch off the Currently for Sale feed."
            >
              <CircleOff size={13} className="mr-1.5" /> Unlist
            </Button>
          )}
          <Button
            onClick={togglePublic}
            variant="outline"
            className={`bg-transparent rounded-sm h-8 ${
              w.public
                ? "border-[#D4AF37] text-[#0A0604] bg-[#D4AF37]/10 hover:bg-[#D4AF37]/20"
                : "border-[#8B6A1F]/45 text-[#3A2A10] hover:bg-[#2B2214]/[0.08] hover:text-[#0A0604]"
            }`}
            data-testid="detail-toggle-public-btn"
            title={w.public ? "Public — anyone with the link can view this restoration page" : "Private — flip ON to share a public restoration page"}
          >
            <Globe size={13} className="mr-1.5" />
            {w.public ? "Public" : "Make Public"}
          </Button>
          {w.public && (
            <Button
              onClick={copyPublicLink}
              variant="outline"
              className="bg-transparent border-[#8B6A1F]/45 text-[#3A2A10] hover:bg-[#2B2214]/[0.08] hover:text-[#0A0604] rounded-sm h-8"
              data-testid="detail-copy-public-link-btn"
              title="Copy /portfolio/… link to clipboard"
            >
              <Link2 size={13} className="mr-1.5" /> Copy Link
            </Button>
          )}
          <Button
            onClick={toggleArchive}
            variant="outline"
            className={`bg-transparent border-[#8B6A1F]/45 text-[#3A2A10] hover:bg-[#2B2214]/[0.08] hover:text-[#0A0604] rounded-sm h-8 ${(w.listing_channel || w.listing_price || w.listed_on || /for sale/i.test(w.status || "")) ? "" : "ml-auto"}`}
            data-testid="detail-archive-watch-btn"
          >
            {w.archived ? (
              <><ArchiveRestore size={13} className="mr-1.5" /> Restore from Archive</>
            ) : (
              <><Archive size={13} className="mr-1.5" /> Archive</>
            )}
          </Button>
          <Button
            onClick={() => setConfirmDelete(true)}
            variant="outline"
            className="bg-transparent border-rose-500/40 text-rose-600 hover:bg-rose-500/10 hover:text-rose-700 rounded-sm h-8"
            data-testid="detail-delete-watch-btn"
          >
            <Trash2 size={13} className="mr-1.5" /> Delete Watch
          </Button>
        </div>
      </div>

      {/* Spec grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-5 border-t border-[#8B6A1F]/35 pt-6 mb-6">
        <Spec label="Movement" value={w.movement_type} />
        <Spec label="Size" value={w.size} />
        <Spec label="Jewels" value={w.jewels} mono />
        <Spec label="BPH" value={w.bph} mono />
        <Spec label="Lift Angle" value={w.lift_angle != null ? `${w.lift_angle}°` : null} mono />
        <Spec label="Shock" value={w.shock} />
        <Spec label="Serial" value={w.serial} mono />
      </div>

      {/* Financial summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-5 border-t border-[#8B6A1F]/35 pt-6 mb-8">
        <Spec label="Purchase" value={fmtMoney(w.purchase_price)} mono />
        <Spec label="Purch. Date" value={w.purch_date || "—"} mono />
        <Spec label="All In" value={fmtMoney(w.all_in)} mono />
        <Spec label="Parts (ledger)" value={fmtMoney(w.parts)} mono />
        <Spec
          label="Total Cost"
          value={fmtMoney((Number(w.all_in) || 0) + (Number(w.parts) || 0))}
          mono
          emphasis
        />
        <Spec label="Min Sale" value={fmtMoney(w.min_sale_price)} mono />
        <Spec label="Listed" value={fmtMoney(w.listing_price)} mono />
        <Spec label="Sold" value={fmtMoney(w.sale_price)} mono />
        <Spec label="P / L" value={fmtMoney(w.profit_loss)} mono accent={w.profit_loss} />
        <Spec label="From" value={w.purchased_from} />
      </div>

      <div className="grid grid-cols-3 gap-5 border-t border-[#8B6A1F]/35 pt-6 mb-8">
        <Stat label="Services" value={services.length} />
        <Stat label="Parts" value={parts.length} sub={partsSpend ? `${fmtMoney(partsSpend)} tracked` : null} />
        <Stat label="Service Spend" value={servicesSpend ? fmtMoney(servicesSpend) : "—"} />
      </div>

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="bg-[#F9F0D4] border border-[#8B6A1F]/35 rounded-sm p-0 h-auto max-w-full overflow-x-auto justify-start">
          <TabsTrigger value="services" className="rounded-none border-r border-[#8B6A1F]/35 px-3 sm:px-5 py-2.5 text-xs uppercase tracking-[0.2em] data-[state=active]:bg-[#2B2214]/[0.14] data-[state=active]:text-[#3A2A10] data-[state=active]:shadow-none" data-testid="tab-services">
            <Wrench size={13} className="mr-2" /> Services
            <span className="ml-2 font-mono-data text-[#3A2A10]">{services.length}</span>
          </TabsTrigger>
          <TabsTrigger value="parts" className="rounded-none px-3 sm:px-5 py-2.5 text-xs uppercase tracking-[0.12em] sm:tracking-[0.2em] data-[state=active]:bg-[#2B2214]/[0.14] data-[state=active]:text-[#3A2A10] data-[state=active]:shadow-none" data-testid="tab-parts">
            <Package size={13} className="mr-2" /> Parts
            <span className="ml-2 font-mono-data text-[#3A2A10]">{parts.length}</span>
          </TabsTrigger>
          <TabsTrigger value="media" className="rounded-none border-l border-[#8B6A1F]/35 px-3 sm:px-5 py-2.5 text-xs uppercase tracking-[0.12em] sm:tracking-[0.2em] data-[state=active]:bg-[#2B2214]/[0.14] data-[state=active]:text-[#3A2A10] data-[state=active]:shadow-none" data-testid="tab-media">
            <Camera size={13} className="mr-2" /> Media
            <span className="ml-2 font-mono-data text-[#3A2A10]">
              {[w.photo_before_front, w.photo_before_back, w.photo_after_front, w.photo_after_back, w.photo_movement_disassembled, w.video_movement].filter(Boolean).length}/6
            </span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="services" className="mt-5">
          <div className="flex items-center justify-between mb-4">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#3A2A10]">Service history for this watch</div>
            <Button onClick={() => setSvcSheet({ open: true, item: null })} className="h-8 px-3 bg-[#D4AF37] hover:bg-[#E5C158] text-[#0A0C10] text-xs uppercase tracking-wider rounded-sm" data-testid="add-service-btn">
              <Plus size={13} className="mr-1.5" /> Log Service
            </Button>
          </div>
          <ServicesTable services={services} onEdit={(s) => setSvcSheet({ open: true, item: s })} onReload={loadServices} />
        </TabsContent>

        <TabsContent value="parts" className="mt-5">
          <div className="flex items-center justify-between mb-4">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#3A2A10]">Parts tracked for this watch</div>
            <Button onClick={() => setPartSheet({ open: true, item: null })} className="h-8 px-3 bg-[#D4AF37] hover:bg-[#E5C158] text-[#0A0C10] text-xs uppercase tracking-wider rounded-sm" data-testid="add-part-btn">
              <Plus size={13} className="mr-1.5" /> Add Part
            </Button>
          </div>
          <PartsTable parts={parts} onEdit={(p) => setPartSheet({ open: true, item: p })} onReload={() => { loadParts(); loadWatch(); }} />
        </TabsContent>

        <TabsContent value="media" className="mt-5">
          <div className="flex items-center justify-between mb-4">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#3A2A10]">Bench-work photos &amp; movement video for this watch</div>
          </div>
          <WatchMediaPanel watch={w} onSaved={loadWatch} />
          <div className="mt-10">
            <RelatedDocumentsPanel
              context="watch"
              watchId={w.id}
              caliber={w.caliber}
              heading="Reference Documents"
            />
          </div>
        </TabsContent>
      </Tabs>

      <WatchFormSheet
        open={editOpen}
        onOpenChange={setEditOpen}
        watch={w}
        validation={validation}
        onSaved={() => { setEditOpen(false); loadWatch(); }}
      />
      <ServiceFormSheet
        open={svcSheet.open}
        onOpenChange={(o) => setSvcSheet({ open: o, item: o ? svcSheet.item : null })}
        service={svcSheet.item}
        watchId={w.id}
        onSaved={() => { setSvcSheet({ open: false, item: null }); loadServices(); loadWatch(); }}
      />
      <InspectionSheet
        open={inspectionOpen}
        onOpenChange={setInspectionOpen}
        watch={w}
        onSaved={loadWatch}
      />
      <PartFormSheet
        open={partSheet.open}
        onOpenChange={(o) => setPartSheet({ open: o, item: o ? partSheet.item : null })}
        part={partSheet.item}
        watchId={w.id}
        onSaved={() => { setPartSheet({ open: false, item: null }); loadParts(); loadWatch(); }}
      />
      <EbayTemplateDialog
        open={ebayOpen}
        onOpenChange={setEbayOpen}
        watch={w}
        servicesCount={services.length}
      />
      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent className="bg-[#F9F0D4] border-[#8B6A1F]/45 rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif-display text-2xl">Delete {w.brand || "this watch"}?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#3A2A10]">
              This will permanently remove <span className="text-[#0A0604]">#{w.excel_id ?? "—"} {w.brand} {w.caliber}</span>.
              Linked parts and services remain in the database but lose their watch reference. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-[#8B6A1F]/45 rounded-sm" data-testid="detail-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-rose-600 hover:bg-rose-700 text-white rounded-sm" data-testid="detail-delete-confirm">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Spec({ label, value, mono, suffix = "", accent, emphasis = false }) {
  const empty = value === null || value === undefined || value === "" || value === "—";
  const accentColor =
    accent === undefined || accent === null
      ? ""
      : accent > 0
      ? "text-emerald-400"
      : accent < 0
      ? "text-rose-400"
      : "";
  return (
    <div className={emphasis ? "border-l-2 border-[#D4AF37] pl-2.5 -ml-1" : ""}>
      <div className={`text-[9px] uppercase tracking-[0.2em] mb-1 ${emphasis ? "text-[#0A0604] font-semibold" : "text-[#3A2A10]"}`}>{label}</div>
      <div className={`${emphasis ? "text-[#0A0604] font-semibold text-base" : "text-[#0A0604]"} ${mono ? "font-mono-data" : ""} ${accentColor}`}>
        {empty ? <span className="text-[#0A0604]">—</span> : `${value}${suffix}`}
      </div>
    </div>
  );
}
function Stat({ label, value, sub }) {
  return (
    <div>
      <div className="text-[9px] uppercase tracking-[0.2em] text-[#3A2A10] mb-1">{label}</div>
      <div className="font-mono-data text-2xl text-[#3A2A10]">{value}</div>
      {sub && <div className="text-[10px] text-[#3A2A10] font-mono-data mt-0.5">{sub}</div>}
    </div>
  );
}
